import { Button } from '@mui/material'
import { PropsWithChildren, memo, useState } from 'react'
import styled from 'styled-components'
import {
  colorPalette,
  isDesaturatedColor,
} from '../../../../style/colorPalette'
import ExpandMore from '@mui/icons-material/ExpandMoreRounded'

// カラム折りたたみ時のサイズ定数
const COLLAPSE_BUTTON_SIZE = 30
const COLUMN_PADDING = 8
const COLUMN_BORDER_WIDTH = 1
export const COLLAPSED_COLUMN_WIDTH =
  COLLAPSE_BUTTON_SIZE + COLUMN_PADDING * 2 + COLUMN_BORDER_WIDTH * 2

type Props = PropsWithChildren<{
  backgroundColor?: string
  isCollapsed?: boolean
  onToggleCollapse?: () => void
}>

const KanbanColumn = memo(
  ({ backgroundColor, children, isCollapsed, onToggleCollapse }: Props) => {
    // 外部から制御される場合は外部状態を使用、そうでなければ内部状態を使用
    const [internalOpen, setInternalOpen] = useState<boolean>(true)
    const isControlled =
      isCollapsed !== undefined && onToggleCollapse !== undefined
    const open = isControlled ? !isCollapsed : internalOpen
    const handleToggle = isControlled
      ? onToggleCollapse
      : () => setInternalOpen(!internalOpen)
    const autocompletedBackgroundColor = colorPalette.monotone[0]
    return (
      <ColumnArea backgroundColor={autocompletedBackgroundColor} open={open}>
        <StyledButton
          variant="contained"
          backgroundColor={autocompletedBackgroundColor}
          open={open}
          onClick={handleToggle}
        >
          <ExpandMore />
        </StyledButton>
        {open && children}
      </ColumnArea>
    )
  }
)

const ColumnArea = styled('div')<{ backgroundColor: string; open: boolean }>(
  props => ({
    position: 'relative',
    height: '100%',
    minWidth: props.open ? '260px' : `${COLLAPSED_COLUMN_WIDTH}px`,
    background: props.backgroundColor,
    padding: `${COLUMN_PADDING}px`,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    flex: props.open ? 1 : 'none',
    borderRadius: '8px',
    border: `${COLUMN_BORDER_WIDTH}px solid ${colorPalette.monotone[2]}`,
  })
)

const StyledButton = styled(Button)<{ backgroundColor: string; open: boolean }>(
  props => {
    const color =
      props.backgroundColor !== colorPalette.monotone[0] &&
      isDesaturatedColor(props.backgroundColor)
        ? colorPalette.monotone[0]
        : colorPalette.monotone[5]
    const defaultStyle = {
      minWidth: 'fit-content',
      width: `${COLLAPSE_BUTTON_SIZE}px`,
      height: `${COLLAPSE_BUTTON_SIZE}px`,
      color,
      background: 'transparent',
      borderRadius: '20%',
      cursor: 'pointer',
      transform: props.open ? 'rotate(90deg)' : 'rotate(270deg)',
      border: '1px solid transparent',
      '&:hover': {
        background: colorPalette.monotone[0],
        border: `1px solid ${colorPalette.monotone[3]}`,
        boxShadow: 'none',
      },
    }
    return props.open
      ? { ...defaultStyle, position: 'absolute', top: 8, right: 8 }
      : defaultStyle
  }
)

export default KanbanColumn
